import React, { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';

import '@tools/sentry';

import { lazyRetry } from '@helpers/common/chunk.helpers';

import Loader from '@components/common/Loader';

import {
  PATH_BRIDGE,
  PATH_CREATE_VAULT,
  PATH_DEX_DAO,
  PATH_DEX_ETF,
  PATH_DEX_ETF_FOUNDS,
  PATH_EDIT_VAULT,
  PATH_GDEX,
  PATH_INDEX,
  PATH_LEADERBOARD,
  PATH_REQUEST_FARM,
  PATH_SERVER_ERROR,
  PATH_SIMILAR_VAULTS,
  PATH_TERMS_AND_CONDITIONS,
  PATH_USDEX,
  PATH_VAULT,
  PATH_VAULTS,
} from '@constants/common/routes.constants';

import App from './App';
import './index.css';

const HomePage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "home" */ '@pages/Home')),
);
const VaultPage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "vault" */ '@pages/Vault')),
);
const SimilarVaultsPage = lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "similar-vaults" */ '@pages/SimilarVaults'),
  ),
);
const EtfPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "etf-page" */ '@pages/ETFs/EtfPage'),
  ),
);
const EtfsPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "etfs-page" */ '@pages/ETFs/EtfsPage'),
  ),
);
const UiKit = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "uikit" */ '@components/common/UiKit'),
  ),
);
const VaultsPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "vaults" */ '@pages/Vaults/VaultsPage'),
  ),
);
const CreateVaultPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "create-vault" */ '@pages/CreateVault'),
  ),
);
const DexDAOPage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "dao" */ '@pages/DexDAO')),
);
const TermsAndConditionsPage = lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "terms-and-conditions" */ '@pages/TermsAndConditions'
      ),
  ),
);
const GDexPage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "gdex" */ '@pages/GDex')),
);
const UsdexPage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "usdex" */ '@pages/Usdex')),
);
const LeaderboardPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "liderboard" */ '@pages/Leaderboard'),
  ),
);
const BridgePage = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "bridge" */ '@pages/Bridge')),
);

const EditVaultPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "editVault" */ '@pages/EditVault'),
  ),
);

const RequestFarmPage = lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "request a farm" */ '@pages/RequestFarm'),
  ),
);

const NotFound = lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "not-found" */ '@components/common/NotFound'),
  ),
);
const ServerError = lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "server-error" */ '@components/common/ServerError'
      ),
  ),
);

const router = createBrowserRouter([
  {
    path: PATH_INDEX,
    element: <App />,
    caseSensitive: false,
    children: [
      {
        path: PATH_INDEX,
        element: <HomePage />,
        caseSensitive: false,
      },
      {
        path: PATH_DEX_ETF,
        element: <EtfsPage />,
        caseSensitive: false,
      },
      {
        path: PATH_DEX_ETF_FOUNDS,
        element: <EtfPage />,
        caseSensitive: false,
      },
      {
        path: PATH_VAULTS,
        element: <VaultsPage />,
        caseSensitive: false,
      },
      // {
      //   path: PATH_VAULT,
      //   element: <VaultPage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_CREATE_VAULT,
      //   element: <CreateVaultPage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_SIMILAR_VAULTS,
      //   element: <SimilarVaultsPage />,
      //   caseSensitive: false,
      // },
      {
        path: PATH_TERMS_AND_CONDITIONS,
        element: <TermsAndConditionsPage />,
        caseSensitive: false,
      },
      {
        path: PATH_GDEX,
        element: <GDexPage />,
        caseSensitive: false,
      },
      {
        path: PATH_USDEX,
        element: <UsdexPage />,
        caseSensitive: false,
      },
      // {
      //   path: PATH_DEX_DAO,
      //   element: <DexDAOPage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_LEADERBOARD,
      //   element: <LeaderboardPage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_EDIT_VAULT,
      //   element: <EditVaultPage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_BRIDGE,
      //   element: <BridgePage />,
      //   caseSensitive: false,
      // },
      // {
      //   path: PATH_REQUEST_FARM,
      //   element: <RequestFarmPage />,
      //   caseSensitive: false,
      // },
      {
        path: PATH_SERVER_ERROR,
        element: <ServerError />,
        caseSensitive: false,
      },
      // {
      //   path: 'ui-kit',
      //   element: <UiKit />,
      //   caseSensitive: false,
      // },
      {
        path: '*',
        element: <NotFound />,
        caseSensitive: false,
      },
    ],
  },
]);

createRoot(document.getElementById('app')!).render(
  <StrictMode>
    <Suspense fallback={<Loader size={42} isCenter />}>
      <RouterProvider router={router} />
    </Suspense>
  </StrictMode>,
);
